import { blue, FaroText, grey } from "@faro-lotv/flat-ui";
import { Box, Stack } from "@mui/material";
import type { JSX } from "react";
import { WizardNavigation } from "./use-wizard-navigation";
import { WizardFooter } from "./wizard-footer";

/** Width of the step panel on large screens */
export const WIZARD_STEP_PANEL_WIDTH = 360;

/**
 * @param navigation The navigation state and actions of the wizard
 * @returns the side panel with the progress, the guidance and controls of the active step, and its actions
 */
export function WizardStepPanel(navigation: WizardNavigation): JSX.Element {
  const { activeStep, activeIndex, steps } = navigation;
  const nextStep = steps.at(activeIndex + 1);

  return (
    <Stack
      component="aside"
      aria-label="workflow step"
      sx={{
        width: WIZARD_STEP_PANEL_WIDTH,
        flexShrink: 0,
        backgroundColor: grey[0],
        borderRight: `1px solid ${grey[200]}`,
        overflow: "hidden",
      }}
    >
      <Stack sx={{ gap: 1, px: 3, pt: 3 }}>
        <Stack
          direction="row"
          sx={{ gap: 0.5 }}
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-valuenow={activeIndex + 1}
          aria-label={`Step ${activeIndex + 1} of ${steps.length}`}
        >
          {steps.map((step, index) => (
            <Box
              key={step.key}
              component="div"
              sx={{
                flex: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor: index <= activeIndex ? blue[500] : grey[200],
              }}
            />
          ))}
        </Stack>
        <Stack direction="row" sx={{ justifyContent: "space-between", gap: 1 }}>
          <FaroText variant="bodyS" color={grey[600]}>
            {`Step ${activeIndex + 1} of ${steps.length}`}
          </FaroText>
          {nextStep && (
            <FaroText variant="bodyS" color={grey[600]} shouldElide>
              {`Next: ${nextStep.label}`}
            </FaroText>
          )}
        </Stack>
        <FaroText variant="heading16">{activeStep.label}</FaroText>
      </Stack>
      <Stack sx={{ flexGrow: 1, overflow: "auto", gap: 2, px: 3, py: 2 }}>
        <FaroText variant="bodyM" color={grey[700]}>
          {activeStep.instructions}
        </FaroText>
        {activeStep.Panel && <activeStep.Panel />}
      </Stack>
      <Stack sx={{ px: 3, pb: 2, borderTop: `1px solid ${grey[200]}` }}>
        <WizardFooter {...navigation} />
      </Stack>
    </Stack>
  );
}
